import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  ProgramApiService,
  Enrollment,
  NextWorkout,
} from '../../services/api/ProgramApiService';
import { useActiveWorkout } from '../../hooks/useActiveWorkout';
import { useWorkout } from '../../contexts/WorkoutContext';
import { getDateKey } from '../../utils';
import { ActiveWorkoutView } from './ActiveWorkoutView';

interface CalendarWorkoutWidgetProps {
  onSessionLogged?: () => void;
}

export const CalendarWorkoutWidget: React.FC<CalendarWorkoutWidgetProps> = ({
  onSessionLogged,
}) => {
  const { t } = useLanguage();
  const { activeWorkout, isInProgress, startWorkout } = useActiveWorkout();
  const { programDays, selectedDate, refreshProgramDays } = useWorkout();

  const todayKey = getDateKey(new Date());
  const isToday = selectedDate ? getDateKey(selectedDate) === todayKey : false;
  const todayHasSession = programDays.has(todayKey);

  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [nextWorkout, setNextWorkout] = useState<NextWorkout | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeWorkoutOpen, setActiveWorkoutOpen] = useState(false);

  const loadEnrollment = useCallback(async () => {
    try {
      const enrollments = await ProgramApiService.getEnrollments();
      const active = enrollments.find((e) => e.status === 'active') ?? null;
      setEnrollment(active);

      if (active && !isInProgress) {
        try {
          const next = await ProgramApiService.getNextWorkout(active.id);
          setNextWorkout(next);
        } catch {
          setNextWorkout(null);
        }
      }
    } catch {
      setEnrollment(null);
    } finally {
      setLoading(false);
    }
  }, [isInProgress]);

  useEffect(() => {
    loadEnrollment();
  }, [loadEnrollment]);

  // After a workout is finished, refresh calendar dots and enrollment data
  const handleSessionLogged = useCallback(() => {
    refreshProgramDays();
    setNextWorkout(null);
    setLoading(true);
    loadEnrollment();
    onSessionLogged?.();
  }, [refreshProgramDays, loadEnrollment, onSessionLogged]);

  if (loading) return null;
  if (!enrollment && !isInProgress) return null;

  // Workout in progress — always show the resume card regardless of programDays
  if (isInProgress && activeWorkout) {
    const totalSets = activeWorkout.exercises.reduce(
      (sum, ex) => sum + ex.sets_data.length,
      0
    );
    const completedSets = activeWorkout.exercises.reduce(
      (sum, ex) => sum + ex.sets_data.filter((s) => s.completed).length,
      0
    );
    const progressValue = totalSets > 0 ? (completedSets / totalSets) * 100 : 0;
    const startedTime = new Date(activeWorkout.startedAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    return (
      <>
        <Card variant="outlined" sx={{ mb: 1.5, borderColor: 'warning.main' }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <ScheduleIcon sx={{ color: 'warning.main', fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('programs.workoutInProgress')}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => setActiveWorkoutOpen(true)}
              >
                {t('programs.resumeWorkout')}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              {activeWorkout.templateName} · {t('programs.startedAt')} {startedTime}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              color="warning"
              sx={{ borderRadius: 1, height: 6 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('programs.setsDone')
                .replace('{done}', String(completedSets))
                .replace('{total}', String(totalSets))}
            </Typography>
          </CardContent>
        </Card>

        <ActiveWorkoutView
          open={activeWorkoutOpen}
          onClose={() => setActiveWorkoutOpen(false)}
          onSessionLogged={handleSessionLogged}
        />
      </>
    );
  }

  // Only show the "Start Workout" option when today's date is selected
  if (!isToday) return null;

  // Today already has a logged program session — don't offer to start another
  if (todayHasSession) return null;

  // No workout in progress, no session today — show "Start Workout" card
  if (!nextWorkout) return null;

  return (
    <>
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FitnessCenterIcon sx={{ color: 'success.main', fontSize: 18 }} />
              <Typography variant="subtitle2" fontWeight="bold">
                {enrollment!.program_name}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => {
                startWorkout(enrollment!.id, nextWorkout);
                setActiveWorkoutOpen(true);
              }}
            >
              {t('programs.startWorkout')}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {t('programs.nextWorkout')}: {nextWorkout.template_name}
          </Typography>

          <Divider sx={{ mb: 0.75 }} />

          {nextWorkout.exercises.map((ex) => (
            <Box
              key={ex.program_exercise_id}
              sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}
            >
              <Typography variant="caption">{ex.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {ex.sets}×{ex.reps} @ {ex.current_weight_kg} kg
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      <ActiveWorkoutView
        open={activeWorkoutOpen}
        onClose={() => setActiveWorkoutOpen(false)}
        onSessionLogged={handleSessionLogged}
      />
    </>
  );
};
